import { User, Bot } from "lucide-react";
import { MarkdownRenderer } from "./MarkdownRenderer";
import type { Message } from "@/types";

interface ChatMessageProps {
  message: Message;
}

/**
 * Chat message bubble component
 * Displays user or assistant messages with appropriate styling
 * Supports Markdown and LaTeX math rendering for assistant messages
 *
 * @param message - Message data to display
 * @returns Styled message bubble
 */
export function ChatMessage({ message }: ChatMessageProps) {
  const isUser = message.role === "user";

  return (
    <div
      className={`flex gap-3 ${isUser ? "flex-row-reverse" : ""} animate-slide-up`}
    >
      {/* Avatar */}
      <div
        className={`
          shrink-0 w-8 h-8 rounded-full flex items-center justify-center
          ${isUser ? "bg-primary-600" : "bg-slate-200"}
        `}
      >
        {isUser ? (
          <User size={16} className="text-white" />
        ) : (
          <Bot size={16} className="text-slate-600" />
        )}
      </div>

      {/* Message bubble */}
      <div
        className={`
          chat-message
          ${isUser ? "chat-message-user" : "chat-message-assistant"}
        `}
      >
        {isUser ? (
          <p className="whitespace-pre-wrap leading-relaxed">{message.content}</p>
        ) : (
          <MarkdownRenderer content={message.content} />
        )}
      </div>
    </div>
  );
}

